import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paintbrush, Droplets, Calendar, Clock, MapPin, CreditCard, ChevronRight, Upload, X, Mail, Info, Check, Edit, CheckCircle } from 'lucide-react';
type Package = {
  name: string;
  sqftRange: string;
  price: number;
  features: string[];
};
export function RequestService() {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [serviceDetails, setServiceDetails] = useState<any>({});
  const [schedule, setSchedule] = useState({
    date: '',
    time: ''
  });
  const [location, setLocation] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('full');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);
  const [additionalRequests, setAdditionalRequests] = useState('');
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  // Package definitions for painting
  const paintingPackages: Package[] = [{
    name: 'Small Unit Package',
    sqftRange: '800 - 1200',
    price: 2500,
    features: ['Green Home 0 VOC paint', 'Professional color consultation', 'Wall preparation and patching', '2-year warranty']
  }, {
    name: 'Medium Unit Package',
    sqftRange: '1201 - 1600',
    price: 3500,
    features: ['Green Home 0 VOC paint', 'Professional color consultation', 'Wall preparation and patching', 'Ceiling painting included', '2-year warranty']
  }, {
    name: 'Large Unit Package',
    sqftRange: '1601 - 2000',
    price: 4500,
    features: ['Green Home 0 VOC paint', 'Professional color consultation', 'Wall preparation and patching', 'Ceiling painting included', 'Premium finish options', '3-year warranty']
  }];
  // Package definitions for waterproofing
  const waterproofingPackages = {
    floor: {
      basic: [{
        name: 'Floor Basic - Small',
        sqftRange: '50 - 100',
        price: 800,
        features: ['Standard waterproofing membrane', 'Single layer application', '24-hour water test', '6-month warranty']
      }, {
        name: 'Floor Basic - Medium',
        sqftRange: '101 - 150',
        price: 1200,
        features: ['Standard waterproofing membrane', 'Single layer application', '24-hour water test', '6-month warranty']
      }],
      advanced: [{
        name: 'Floor Advanced - Small',
        sqftRange: '50 - 100',
        price: 1200,
        features: ['Premium waterproofing membrane', 'Multi-layer application', '48-hour water test', '1-year warranty']
      }, {
        name: 'Floor Advanced - Medium',
        sqftRange: '101 - 150',
        price: 1800,
        features: ['Premium waterproofing membrane', 'Multi-layer application', '48-hour water test', '1-year warranty']
      }]
    },
    wall: {
      basic: [{
        name: 'Wall Basic - Small',
        sqftRange: '50 - 100',
        price: 900,
        features: ['Standard waterproofing coating', 'Single layer application', 'Crack sealing', '6-month warranty']
      }, {
        name: 'Wall Basic - Medium',
        sqftRange: '101 - 150',
        price: 1400,
        features: ['Standard waterproofing coating', 'Single layer application', 'Crack sealing', '6-month warranty']
      }],
      advanced: [{
        name: 'Wall Advanced - Small',
        sqftRange: '50 - 100',
        price: 1400,
        features: ['Premium waterproofing coating', 'Multi-layer application', 'Crack repair and sealing', '1-year warranty']
      }, {
        name: 'Wall Advanced - Medium',
        sqftRange: '101 - 150',
        price: 2000,
        features: ['Premium waterproofing coating', 'Multi-layer application', 'Crack repair and sealing', '1-year warranty']
      }]
    },
    both: {
      basic: [{
        name: 'Floor & Wall Basic - Small',
        sqftRange: '50 - 100',
        price: 1500,
        features: ['Standard waterproofing for floor and wall', 'Single layer application', '24-hour water test', '6-month warranty']
      }, {
        name: 'Floor & Wall Basic - Medium',
        sqftRange: '101 - 150',
        price: 2200,
        features: ['Standard waterproofing for floor and wall', 'Single layer application', '24-hour water test', '6-month warranty']
      }],
      advanced: [{
        name: 'Floor & Wall Advanced - Small',
        sqftRange: '50 - 100',
        price: 2200,
        features: ['Premium waterproofing for floor and wall', 'Multi-layer application', '48-hour water test', '1-year warranty']
      }, {
        name: 'Floor & Wall Advanced - Medium',
        sqftRange: '101 - 150',
        price: 3200,
        features: ['Premium waterproofing for floor and wall', 'Multi-layer application', '48-hour water test', '1-year warranty']
      }]
    }
  };
  const services = [{
    id: 'painting',
    name: 'Painting Services',
    description: 'Professional painting with eco-friendly 0 VOC paints',
    icon: <Paintbrush className="h-8 w-8 text-primary" data-id="element-1187" />
  }, {
    id: 'waterproofing',
    name: 'Waterproofing',
    description: 'Complete moisture protection solutions for your home',
    icon: <Droplets className="h-8 w-8 text-primary" data-id="element-1188" />
  }];
  const handleServiceSelect = (serviceId: string) => {
    setSelectedService(serviceId);
    setStep(2);
    setSelectedPackage(null);
    setServiceDetails({});
  };
  const handleServiceDetailsChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setServiceDetails(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset package selection when details change
    if (name === 'packageType' || name === 'quality' || name === 'sqft') {
      setSelectedPackage(null);
    }
  };
  const handlePackageSelect = (pkg: Package) => {
    setSelectedPackage(pkg);
  };
  const getAvailablePackages = (): Package[] => {
    if (selectedService === 'painting') {
      return paintingPackages;
    } else if (selectedService === 'waterproofing') {
      const packageType = serviceDetails.packageType;
      const quality = serviceDetails.quality;
      if (packageType && quality) {
        return waterproofingPackages[packageType as keyof typeof waterproofingPackages]?.[quality as 'basic' | 'advanced'] || [];
      }
    }
    return [];
  };
  const getSuggestedPackage = (pkg: Package): boolean => {
    const sqft = parseInt(serviceDetails.sqft);
    if (!sqft) return false;
    const [min, max] = pkg.sqftRange.split(' - ').map(Number);
    return sqft >= min && sqft <= max;
  };
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setUploadedPhotos(prev => [...prev, ...newFiles]);
    }
  };
  const removePhoto = (index: number) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
  };
  const handleScheduleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setSchedule(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleLocationChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocation(e.target.value);
  };
  const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentMethod(e.target.value);
  };
  const handleContinueToReview = () => {
    setStep(3);
  };
  const handleSubmit = () => {
    setIsSubmitting(true);
    const serviceRequest = {
      serviceType: selectedService,
      serviceDetails,
      selectedPackage,
      schedule,
      location,
      paymentMethod,
      uploadedPhotos: uploadedPhotos.map(f => f.name),
      additionalRequests,
      createdAt: new Date().toISOString()
    };
    setTimeout(() => {
      setIsSubmitting(false);
      navigate('/customer/payment', {
        state: {
          serviceRequest
        }
      });
    }, 1000);
  };
  const getTimeLabel = (time: string) => {
    switch (time) {
      case 'morning':
        return 'Morning (8:00 AM - 12:00 PM)';
      case 'afternoon':
        return 'Afternoon (12:00 PM - 4:00 PM)';
      case 'evening':
        return 'Evening (4:00 PM - 8:00 PM)';
      default:
        return time;
    }
  };
  const isStep2Valid = () => {
    const hasSchedule = schedule.date && schedule.time;
    const hasLocation = location.trim().length > 0;
    const hasPackage = selectedPackage !== null;
    if (selectedService === 'painting') {
      return hasSchedule && hasLocation && serviceDetails.unitType && serviceDetails.sqft && hasPackage;
    } else if (selectedService === 'waterproofing') {
      return hasSchedule && hasLocation && serviceDetails.packageType && serviceDetails.quality && serviceDetails.sqft && hasPackage;
    }
    return false;
  };
  const renderStepOne = () => <div data-id="element-1189">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-1190">
        Select a Service
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-1191">
        {services.map(service => <div key={service.id} className={`border-2 rounded-xl p-8 cursor-pointer transition-all ${selectedService === service.id ? 'border-primary bg-primary/5 shadow-lg' : 'border-gray-200 hover:border-primary/50 hover:shadow-md'}`} onClick={() => handleServiceSelect(service.id)} data-id="element-1192">
            <div className="flex flex-col items-center text-center" data-id="element-1193">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-4" data-id="element-1194">
                {service.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1195">
                {service.name}
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1196">{service.description}</p>
            </div>
          </div>)}
      </div>
    </div>;
  const renderStepTwo = () => {
    const availablePackages = getAvailablePackages();
    const showPackages = selectedService === 'painting' ? serviceDetails.unitType && serviceDetails.sqft : serviceDetails.packageType && serviceDetails.quality && serviceDetails.sqft;
    return <div data-id="element-1197">
        <button onClick={() => setStep(1)} className="flex items-center text-primary mb-6 hover:underline font-medium" data-id="element-1198">
          <ChevronRight className="h-4 w-4 mr-1 rotate-180" data-id="element-1199" />
          Back to services
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1200">
          {selectedService === 'painting' && 'Request Painting Service'}
          {selectedService === 'waterproofing' && 'Request Waterproofing Service'}
        </h2>
        <p className="text-gray-600 mb-6" data-id="element-1201">
          Configure your service and select a package that fits your needs
        </p>
        {/* Timeline Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8" data-id="element-1202">
          <div className="flex items-start" data-id="element-1203">
            <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" data-id="element-1204" />
            <div className="text-sm text-blue-800" data-id="element-1205">
              <p className="font-semibold mb-1" data-id="element-1206">Service Timeline:</p>
              <p data-id="element-1207">• 2-3 working days for inspection after order confirmation</p>
              <p data-id="element-1208">• 5-7 working days for service completion after inspection</p>
            </div>
          </div>
        </div>
        <div className="space-y-8" data-id="element-1209">
          {/* Service Configuration */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1210">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center" data-id="element-1211">
              <span className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold mr-3 text-sm" data-id="element-1212">
                1
              </span>
              Service Configuration
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1213">
              {selectedService === 'painting' && <>
                  <div data-id="element-1214">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1215">
                      Unit Type <span className="text-red-500" data-id="element-1216">*</span>
                    </label>
                    <select name="unitType" className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary focus:ring-primary text-gray-900 px-4 py-2.5" onChange={handleServiceDetailsChange} value={serviceDetails.unitType || ''} data-id="element-1217">
                      <option value="" data-id="element-1218">Select unit type</option>
                      <option value="landed" data-id="element-1219">Landed Property</option>
                      <option value="condo" data-id="element-1220">Condominium</option>
                      <option value="apartment" data-id="element-1221">Apartment</option>
                    </select>
                  </div>
                  <div data-id="element-1222">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1223">
                      Square Footage (sqft){' '}
                      <span className="text-red-500" data-id="element-1224">*</span>
                    </label>
                    <input name="sqft" type="number" className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary focus:ring-primary text-gray-900 px-4 py-2.5" placeholder="Enter square footage" onChange={handleServiceDetailsChange} value={serviceDetails.sqft || ''} data-id="element-1225" />
                  </div>
                </>}
              {selectedService === 'waterproofing' && <>
                  <div data-id="element-1226">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1227">
                      Package Type <span className="text-red-500" data-id="element-1228">*</span>
                    </label>
                    <select name="packageType" className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary focus:ring-primary text-gray-900 px-4 py-2.5" onChange={handleServiceDetailsChange} value={serviceDetails.packageType || ''} data-id="element-1229">
                      <option value="" data-id="element-1230">Select package type</option>
                      <option value="floor" data-id="element-1231">Floor Only</option>
                      <option value="wall" data-id="element-1232">Wall Only</option>
                      <option value="both" data-id="element-1233">Floor & Wall</option>
                    </select>
                  </div>
                  <div data-id="element-1234">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1235">
                      Quality Level <span className="text-red-500" data-id="element-1236">*</span>
                    </label>
                    <select name="quality" className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary focus:ring-primary text-gray-900 px-4 py-2.5" onChange={handleServiceDetailsChange} value={serviceDetails.quality || ''} data-id="element-1237">
                      <option value="" data-id="element-1238">Select quality level</option>
                      <option value="basic" data-id="element-1239">Basic</option>
                      <option value="advanced" data-id="element-1240">Advanced</option>
                    </select>
                  </div>
                  <div data-id="element-1241">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1242">
                      Square Footage (sqft){' '}
                      <span className="text-red-500" data-id="element-1243">*</span>
                    </label>
                    <input name="sqft" type="number" className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary focus:ring-primary text-gray-900 px-4 py-2.5" placeholder="Enter square footage" onChange={handleServiceDetailsChange} value={serviceDetails.sqft || ''} data-id="element-1244" />
                  </div>
                </>}
            </div>
          </div>
          {/* Package Selection */}
          {showPackages && availablePackages.length > 0 && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1245">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center" data-id="element-1246">
                <span className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold mr-3 text-sm" data-id="element-1247">
                  2
                </span>
                Select Your Package
              </h3>
              <p className="text-sm text-gray-600 mb-6 ml-11" data-id="element-1248">
                Choose the package that best fits your {serviceDetails.sqft}{' '}
                sqft space
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1249">
                {availablePackages.map((pkg, index) => {
              const isSelected = selectedPackage?.name === pkg.name;
              const isSuggested = getSuggestedPackage(pkg);
              return <div key={index} onClick={() => handlePackageSelect(pkg)} className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${isSelected ? 'border-primary bg-primary/5 shadow-lg' : 'border-gray-200 hover:border-primary/50 hover:shadow-md'}`} data-id="element-1250">
                      {isSuggested && <div className="absolute -top-3 left-1/2 transform -translate-x-1/2" data-id="element-1251">
                          <span className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full" data-id="element-1252">
                            Recommended
                          </span>
                        </div>}
                      {isSelected && <div className="absolute top-4 right-4" data-id="element-1253">
                          <CheckCircle className="h-6 w-6 text-primary" data-id="element-1254" />
                        </div>}
                      <div className="mb-4" data-id="element-1255">
                        <h4 className="text-lg font-bold text-gray-900 mb-1" data-id="element-1256">
                          {pkg.name}
                        </h4>
                        <p className="text-sm text-gray-600 mb-3" data-id="element-1257">
                          {pkg.sqftRange} sqft
                        </p>
                        <p className="text-3xl font-bold text-primary" data-id="element-1258">
                          MYR {pkg.price.toFixed(2)}
                        </p>
                      </div>
                      <ul className="space-y-2" data-id="element-1259">
                        {pkg.features.map((feature, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-1260">
                            <Check className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" data-id="element-1261" />
                            <span data-id="element-1262">{feature}</span>
                          </li>)}
                      </ul>
                    </div>;
            })}
              </div>
            </div>}
          {/* Additional Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1263">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center" data-id="element-1264">
              <span className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold mr-3 text-sm" data-id="element-1265">
                {showPackages ? '3' : '2'}
              </span>
              Schedule & Location
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1266">
              <div data-id="element-1267">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1268">
                  Preferred Date <span className="text-red-500" data-id="element-1269">*</span>
                </label>
                <div className="relative" data-id="element-1270">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1271" />
                  <input type="date" name="date" className="w-full pl-10 pr-4 py-2.5 border-gray-300 rounded-lg shadow-sm focus:border-primary focus:ring-primary text-gray-900" onChange={handleScheduleChange} value={schedule.date} data-id="element-1272" />
                </div>
              </div>
              <div data-id="element-1273">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1274">
                  Preferred Time <span className="text-red-500" data-id="element-1275">*</span>
                </label>
                <div className="relative" data-id="element-1276">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1277" />
                  <select name="time" className="w-full pl-10 pr-4 py-2.5 border-gray-300 rounded-lg shadow-sm focus:border-primary focus:ring-primary text-gray-900" onChange={handleScheduleChange} value={schedule.time} data-id="element-1278">
                    <option value="" data-id="element-1279">Select time</option>
                    <option value="morning" data-id="element-1280">
                      Morning (8:00 AM - 12:00 PM)
                    </option>
                    <option value="afternoon" data-id="element-1281">
                      Afternoon (12:00 PM - 4:00 PM)
                    </option>
                    <option value="evening" data-id="element-1282">Evening (4:00 PM - 8:00 PM)</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="mb-6" data-id="element-1283">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1284">
                Service Address <span className="text-red-500" data-id="element-1285">*</span>
              </label>
              <div className="relative" data-id="element-1286">
                <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" data-id="element-1287" />
                <textarea rows={3} className="w-full pl-10 pr-4 py-2.5 border-gray-300 rounded-lg shadow-sm focus:border-primary focus:ring-primary text-gray-900" placeholder="Enter your full address" onChange={handleLocationChange} value={location} data-id="element-1288"></textarea>
              </div>
            </div>
            {/* Photo Upload */}
            <div className="mb-6" data-id="element-1289">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1290">
                Upload Photos (Optional)
              </label>
              <label className="flex justify-center px-6 py-8 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors" data-id="element-1291">
                <div className="space-y-2 text-center" data-id="element-1292">
                  <Upload className="mx-auto h-10 w-10 text-gray-400" data-id="element-1293" />
                  <div className="text-sm text-gray-600" data-id="element-1294">
                    <span className="font-semibold text-primary" data-id="element-1295">
                      Upload files
                    </span>
                    <span className="ml-1" data-id="element-1296">or drag and drop</span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-1297">PNG, JPG up to 10MB</p>
                </div>
                <input type="file" className="sr-only" multiple accept="image/*" onChange={handlePhotoUpload} data-id="element-1298" />
              </label>
              {uploadedPhotos.length > 0 && <div className="mt-3 space-y-2" data-id="element-1299">
                  {uploadedPhotos.map((file, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1300">
                      <span className="text-sm text-gray-700 truncate flex-1" data-id="element-1301">
                        {file.name}
                      </span>
                      <button onClick={() => removePhoto(index)} className="ml-3 text-red-500 hover:text-red-700" data-id="element-1302">
                        <X className="h-4 w-4" data-id="element-1303" />
                      </button>
                    </div>)}
                </div>}
            </div>
            {/* Additional Requests */}
            <div data-id="element-1304">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1305">
                Additional Requests (May incur extra charges)
              </label>
              <textarea rows={3} className="w-full px-4 py-2.5 border-gray-300 rounded-lg shadow-sm focus:border-primary focus:ring-primary text-gray-900" placeholder="Describe any additional work needed..." value={additionalRequests} onChange={e => setAdditionalRequests(e.target.value)} data-id="element-1306"></textarea>
            </div>
            {/* Custom Service Option */}
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-1307">
              <p className="text-sm text-gray-700 mb-2 font-medium" data-id="element-1308">
                Need a custom service or package?
              </p>
              <a href="mailto:support@smarthomebox.my?subject=Custom Service Request" className="inline-flex items-center text-primary hover:text-primary/80 text-sm font-semibold" data-id="element-1309">
                <Mail className="h-4 w-4 mr-1" data-id="element-1310" />
                Contact us for personalized options
              </a>
            </div>
          </div>
          {/* Payment Method */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1311">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center" data-id="element-1312">
              <span className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold mr-3 text-sm" data-id="element-1313">
                {showPackages ? '4' : '3'}
              </span>
              Payment Method
            </h3>
            <div className="space-y-3 mb-4" data-id="element-1314">
              <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary transition-colors" data-id="element-1315">
                <input type="radio" name="payment" value="full" className="h-4 w-4 text-primary focus:ring-primary border-gray-300" checked={paymentMethod === 'full'} onChange={handlePaymentMethodChange} data-id="element-1316" />
                <span className="ml-3 text-sm font-medium text-gray-900" data-id="element-1317">
                  Pay in full
                </span>
              </label>
              <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary transition-colors" data-id="element-1318">
                <input type="radio" name="payment" value="installment" className="h-4 w-4 text-primary focus:ring-primary border-gray-300" checked={paymentMethod === 'installment'} onChange={handlePaymentMethodChange} data-id="element-1319" />
                <span className="ml-3 text-sm font-medium text-gray-900" data-id="element-1320">
                  3-month 0% installment
                </span>
              </label>
            </div>
            <div className="flex items-center text-sm text-gray-600 bg-blue-50 p-3 rounded-lg" data-id="element-1321">
              <CreditCard className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" data-id="element-1322" />
              <span data-id="element-1323">You'll proceed to payment after reviewing your order</span>
            </div>
          </div>
          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4" data-id="element-1324">
            <button onClick={() => setStep(1)} className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors" data-id="element-1325">
              Back
            </button>
            <div className="flex flex-col items-end" data-id="element-1326">
              <button className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors" onClick={handleContinueToReview} disabled={!isStep2Valid()} data-id="element-1327">
                Continue to Review
                <ChevronRight className="h-5 w-5 ml-2" data-id="element-1328" />
              </button>
              {!isStep2Valid() && <p className="text-sm text-red-600 mt-2" data-id="element-1329">
                  Please complete all required fields
                </p>}
            </div>
          </div>
        </div>
      </div>;
  };
  const renderStepThree = () => {
    return <div data-id="element-1330">
        <button onClick={() => setStep(2)} className="flex items-center text-primary mb-6 hover:underline font-medium" data-id="element-1331">
          <ChevronRight className="h-4 w-4 mr-1 rotate-180" data-id="element-1332" />
          Back to configuration
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1333">
          Review Your Order
        </h2>
        <p className="text-gray-600 mb-6" data-id="element-1334">
          Please review your service request before proceeding to payment
        </p>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-1335">
          {/* Service Package Summary */}
          <div className="p-6 border-b border-gray-200" data-id="element-1336">
            <div className="flex items-start justify-between mb-4" data-id="element-1337">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1338">
                Service Package
              </h3>
              <button onClick={() => setStep(2)} className="flex items-center text-sm text-primary hover:text-primary/80 font-medium" data-id="element-1339">
                <Edit className="h-4 w-4 mr-1" data-id="element-1340" />
                Edit
              </button>
            </div>
            {selectedPackage && <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg p-6 border border-primary/20" data-id="element-1341">
                <div className="flex items-start justify-between mb-4" data-id="element-1342">
                  <div data-id="element-1343">
                    <p className="text-xl font-bold text-gray-900" data-id="element-1344">
                      {selectedPackage.name}
                    </p>
                    <p className="text-sm text-gray-600 mt-1" data-id="element-1345">
                      {selectedPackage.sqftRange} sqft
                    </p>
                  </div>
                  <p className="text-3xl font-bold text-primary" data-id="element-1346">
                    MYR {selectedPackage.price.toFixed(2)}
                  </p>
                </div>
                <div className="space-y-2" data-id="element-1347">
                  {selectedPackage.features.map((feature, index) => <div key={index} className="flex items-start" data-id="element-1348">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" data-id="element-1349" />
                      <span className="text-sm text-gray-700" data-id="element-1350">{feature}</span>
                    </div>)}
                </div>
              </div>}
            {/* Service Details */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1351">
              {selectedService === 'painting' && <>
                  <div className="p-4 bg-gray-50 rounded-lg" data-id="element-1352">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1353">
                      Unit Type
                    </p>
                    <p className="text-sm text-gray-900 font-medium capitalize" data-id="element-1354">
                      {serviceDetails.unitType?.replace('_', ' ') || 'Not specified'}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg" data-id="element-1355">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1356">
                      Square Footage
                    </p>
                    <p className="text-sm text-gray-900 font-medium" data-id="element-1357">
                      {serviceDetails.sqft} sqft
                    </p>
                  </div>
                </>}
              {selectedService === 'waterproofing' && <>
                  <div className="p-4 bg-gray-50 rounded-lg" data-id="element-1358">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1359">
                      Package Type
                    </p>
                    <p className="text-sm text-gray-900 font-medium capitalize" data-id="element-1360">
                      {serviceDetails.packageType?.replace('_', ' ') || 'Not specified'}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg" data-id="element-1361">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1362">
                      Quality Level
                    </p>
                    <p className="text-sm text-gray-900 font-medium capitalize" data-id="element-1363">
                      {serviceDetails.quality || 'Not specified'}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg" data-id="element-1364">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1365">
                      Square Footage
                    </p>
                    <p className="text-sm text-gray-900 font-medium" data-id="element-1366">
                      {serviceDetails.sqft} sqft
                    </p>
                  </div>
                </>}
            </div>
          </div>
          {/* Schedule & Location */}
          <div className="p-6 border-b border-gray-200" data-id="element-1367">
            <div className="flex items-start justify-between mb-4" data-id="element-1368">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1369">
                Schedule & Location
              </h3>
              <button onClick={() => setStep(2)} className="flex items-center text-sm text-primary hover:text-primary/80 font-medium" data-id="element-1370">
                <Edit className="h-4 w-4 mr-1" data-id="element-1371" />
                Edit
              </button>
            </div>
            <div className="space-y-4" data-id="element-1372">
              <div className="flex items-start p-4 bg-gray-50 rounded-lg" data-id="element-1373">
                <Calendar className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-1374" />
                <div data-id="element-1375">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1376">
                    Preferred Date
                  </p>
                  <p className="text-sm text-gray-900 font-medium" data-id="element-1377">
                    {new Date(schedule.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  </p>
                </div>
              </div>
              <div className="flex items-start p-4 bg-gray-50 rounded-lg" data-id="element-1378">
                <Clock className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-1379" />
                <div data-id="element-1380">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1381">
                    Preferred Time
                  </p>
                  <p className="text-sm text-gray-900 font-medium" data-id="element-1382">
                    {getTimeLabel(schedule.time)}
                  </p>
                </div>
              </div>
              <div className="flex items-start p-4 bg-gray-50 rounded-lg" data-id="element-1383">
                <MapPin className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-1384" />
                <div data-id="element-1385">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1386">
                    Service Address
                  </p>
                  <p className="text-sm text-gray-900 font-medium" data-id="element-1387">
                    {location}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Additional Information */}
          {(uploadedPhotos.length > 0 || additionalRequests) && <div className="p-6 border-b border-gray-200" data-id="element-1388">
              <div className="flex items-start justify-between mb-4" data-id="element-1389">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-1390">
                  Additional Information
                </h3>
                <button onClick={() => setStep(2)} className="flex items-center text-sm text-primary hover:text-primary/80 font-medium" data-id="element-1391">
                  <Edit className="h-4 w-4 mr-1" data-id="element-1392" />
                  Edit
                </button>
              </div>
              {uploadedPhotos.length > 0 && <div className="mb-4" data-id="element-1393">
                  <p className="text-sm font-semibold text-gray-700 mb-2" data-id="element-1394">
                    Uploaded Photos ({uploadedPhotos.length})
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-1395">
                    {uploadedPhotos.map((file, index) => <div key={index} className="px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700" data-id="element-1396">
                        {file.name}
                      </div>)}
                  </div>
                </div>}
              {additionalRequests && <div data-id="element-1397">
                  <p className="text-sm font-semibold text-gray-700 mb-2" data-id="element-1398">
                    Additional Requests
                  </p>
                  <p className="text-sm text-gray-900 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1399">
                    {additionalRequests}
                  </p>
                </div>}
            </div>}
          {/* Payment Summary */}
          <div className="p-6" data-id="element-1400">
            <div className="flex items-start justify-between mb-4" data-id="element-1401">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1402">
                Payment Summary
              </h3>
              <button onClick={() => setStep(2)} className="flex items-center text-sm text-primary hover:text-primary/80 font-medium" data-id="element-1403">
                <Edit className="h-4 w-4 mr-1" data-id="element-1404" />
                Edit
              </button>
            </div>
            <div className="space-y-3" data-id="element-1405">
              <div className="flex justify-between text-sm py-2" data-id="element-1406">
                <span className="text-gray-600" data-id="element-1407">Service Package</span>
                <span className="text-gray-900 font-semibold" data-id="element-1408">
                  MYR {selectedPackage?.price.toFixed(2)}
                </span>
              </div>
              {additionalRequests && <div className="flex justify-between text-sm py-2" data-id="element-1409">
                  <span className="text-gray-600" data-id="element-1410">Additional Requests</span>
                  <span className="text-gray-900 font-semibold" data-id="element-1411">TBD</span>
                </div>}
              <div className="border-t-2 border-gray-200 pt-3 flex justify-between items-center" data-id="element-1412">
                <span className="text-lg font-semibold text-gray-900" data-id="element-1413">
                  Total
                </span>
                <span className="text-3xl font-bold text-primary" data-id="element-1414">
                  MYR {selectedPackage?.price.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-700 bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4" data-id="element-1415">
                <CreditCard className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" data-id="element-1416" />
                <span data-id="element-1417">
                  Payment Method:{' '}
                  <span className="font-semibold text-gray-900" data-id="element-1418">
                    {paymentMethod === 'full' ? 'Pay in full' : '3-month 0% installment'}
                  </span>
                </span>
              </div>
            </div>
          </div>
          {/* Action Buttons */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-1419">
            <div className="flex justify-between items-center" data-id="element-1420">
              <button onClick={() => setStep(2)} className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors" data-id="element-1421">
                Back to Edit
              </button>
              <button className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors" onClick={handleSubmit} disabled={isSubmitting} data-id="element-1422">
                {isSubmitting ? <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1423">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1424"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1425"></path>
                    </svg>
                    Processing...
                  </> : <>
                    Proceed to Payment
                    <ChevronRight className="h-5 w-5 ml-2" data-id="element-1426" />
                  </>}
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="light" data-id="element-1427">
      <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1428">Request Service</h1>
      {/* Progress Indicator */}
      <div className="mb-8" data-id="element-1429">
        <div className="flex items-center" data-id="element-1430">
          <div className={`flex items-center justify-center h-10 w-10 rounded-full font-semibold ${step >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-1431">
            1
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`} data-id="element-1432"></div>
          <div className={`flex items-center justify-center h-10 w-10 rounded-full font-semibold ${step >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-1433">
            2
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-primary' : 'bg-gray-200'}`} data-id="element-1434"></div>
          <div className={`flex items-center justify-center h-10 w-10 rounded-full font-semibold ${step >= 3 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-1435">
            3
          </div>
          <div className="flex-1 h-1 mx-2 bg-gray-200" data-id="element-1436"></div>
          <div className="flex items-center justify-center h-10 w-10 rounded-full font-semibold bg-gray-200 text-gray-600" data-id="element-1437">
            4
          </div>
        </div>
        <div className="flex justify-between mt-3 text-sm" data-id="element-1438">
          <div className={step >= 1 ? 'text-primary font-semibold' : 'text-gray-500'} data-id="element-1439">
            Select Service
          </div>
          <div className={step >= 2 ? 'text-primary font-semibold' : 'text-gray-500'} data-id="element-1440">
            Configure
          </div>
          <div className={step >= 3 ? 'text-primary font-semibold' : 'text-gray-500'} data-id="element-1441">
            Review
          </div>
          <div className="text-gray-500" data-id="element-1442">Payment</div>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8" data-id="element-1443">
        {step === 1 && renderStepOne()}
        {step === 2 && renderStepTwo()}
        {step === 3 && renderStepThree()}
      </div>
    </div>;
}